import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormControl} from '@angular/forms';
import {
  ImportModuleLookupCheck,
  ImportModuleLookupKeyLabels,
  ImportModuleSourceColumn,
} from '../../../core/definitions/import-module';
import {LookupColumnProblem} from '../import-session.store';
import {
  isLookupComplete,
  LookupDefinition,
  lookupDisplayName,
  lookupKeyOptionsInUse,
  lookupPayload,
  removeAlias,
  setAlias,
} from '../lookup-controls';
import {
  checkLabelColumns,
  keyLabelText,
  listsEveryCode,
  LookupCodeRow,
  lookupCodeRows,
  matchingTableKeys,
} from './lookup-codes';

/** A code of a lookup, for the page to act on. */
export interface LookupCodeEvent {
  lookup: LookupDefinition;
  code: string;
}

export type LookupCodeFilter = 'all' | 'misses';

/** Rows the code table shows under either filter before "Vis alle". */
const CODE_ROWS_SHOWN = 100;
/** The most frequent codes shown under Vanligste koder. */
const TOP_CODES_SHOWN = 5;
/** Table keys the alias picker offers at a time. */
const TABLE_KEYS_OFFERED = 50;

/**
 * The Kodeoppslag tab's lookups, one line each: its name, the code column
 * and the table key it is looked up by, its hits over the whole file and
 * where the mapping picks it. An open line holds the lookup's own fields,
 * its codes as the last check found them - the misses with what they
 * tried and how to fix them, and beside them the most frequent hits with
 * their labels - the key options, and its removal.
 *
 * The page owns the lookups, their table columns and their checks; this
 * component edits the lookups' own controls, and asks the page to remove
 * one or to read one of its codes as blank.
 */
@Component({
  selector: 'app-lookup-definitions',
  templateUrl: './lookup-definitions.component.html',
  styleUrls: ['./lookup-definitions.component.scss'],
  standalone: false,
})
export class LookupDefinitionsComponent {
  @Input() lookups: LookupDefinition[] = [];
  // The sibling tables a lookup can read, and their columns once loaded.
  @Input() tables: string[] = [];
  @Input() tableColumns: { [table: string]: ImportModuleSourceColumn[] } = {};
  @Input() tableColumnsLoading: ReadonlySet<string> = new Set<string>();
  // The tables whose columns failed to load, with the error it gave.
  @Input() tableColumnsFailed: ReadonlyMap<string, string> = new Map();
  // The source table's columns - where a code is read from.
  @Input() columns: ImportModuleSourceColumn[] = [];
  // Each lookup's last check, by lookup key; `checkPending` while a newer
  // one is waiting or on its way, `checkFailed` while the newest one
  // failed. Either way the checks shown may not fit the lookups as they
  // are.
  @Input() checks: ReadonlyMap<string, ImportModuleLookupCheck> = new Map();
  @Input() checkPending = false;
  @Input() checkFailed = false;
  // The lookups whose table lacks a column they read, by lookup key; they
  // are not checked.
  @Input() columnProblems: ReadonlyMap<string, LookupColumnProblem> = new Map();
  // Where the mapping picks each lookup, by lookup key.
  @Input() usages: ReadonlyMap<string, string[]> = new Map();
  // The key of the open line; `null` while all are closed.
  @Input() expanded: string | null = null;

  @Output() expandedChange = new EventEmitter<string | null>();
  @Output() remove = new EventEmitter<LookupDefinition>();
  @Output() meansBlank = new EventEmitter<LookupCodeEvent>();
  // A table whose columns the page should load again.
  @Output() retryColumns = new EventEmitter<string>();

  readonly keyLabelText = keyLabelText;

  // What each open line shows, by lookup key: the code filter the
  // operator picked, whether the code table shows every row of its
  // filter, whether the key options are open.
  private readonly codeFilters = new Map<string, LookupCodeFilter>();
  private readonly allCodesShown = new Set<string>();
  private readonly keyOptionsOpen = new Set<string>();
  // The one missed code whose table-key picker is open, and its search
  // text. The other miss rows show a button in its place.
  pickerTarget: LookupCodeEvent | null = null;
  readonly pickerControl = new FormControl<string>('', {nonNullable: true});
  private pickerOptionCache: {
    check: ImportModuleLookupCheck; text: string; options: ImportModuleLookupKeyLabels[];
  } | null = null;
  // The source table's column names, for one `columns` array.
  private columnNameCache: {columns: ImportModuleSourceColumn[]; names: Set<string>} | null = null;
  // Each lookup's code rows, its misses, its most frequent hits and
  // whether the check left codes out, built once per check and per
  // setting of the aliases, the key options and case-insensitive matching.
  private readonly codeRowCache = new Map<string, {
    check: ImportModuleLookupCheck; aliases: string; keyOptions: boolean; caseInsensitive: boolean;
    rows: LookupCodeRow[]; misses: LookupCodeRow[]; topHits: LookupCodeRow[]; capped: boolean;
  }>();

  isComplete(lookup: LookupDefinition): boolean {
    return isLookupComplete(lookup);
  }

  displayName(lookup: LookupDefinition): string {
    return lookupDisplayName(lookup);
  }

  keyColumns(lookup: LookupDefinition): ImportModuleSourceColumn[] {
    return this.tableColumns[lookup.tableControl.value ?? ''] ?? [];
  }

  isLoadingKeyColumns(lookup: LookupDefinition): boolean {
    const table = lookup.tableControl.value;
    return !!table && this.tableColumnsLoading.has(table);
  }

  /** The error the lookup's table columns failed to load with, `null`
   * while they did not fail. */
  columnsFailure(lookup: LookupDefinition): string | null {
    const table = lookup.tableControl.value;
    return table ? this.tableColumnsFailed.get(table) ?? null : null;
  }

  usagesText(lookup: LookupDefinition): string {
    return (this.usages.get(lookup.key) ?? []).join(', ');
  }

  toggle(lookup: LookupDefinition): void {
    this.expanded = this.expanded === lookup.key ? null : lookup.key;
    this.expandedChange.emit(this.expanded);
  }

  /** The lookup's last check, while it is complete and its table has the
   * columns it reads. */
  checkOf(lookup: LookupDefinition): ImportModuleLookupCheck | null {
    return this.isComplete(lookup) && !this.columnProblems.has(lookup.key)
      ? this.checks.get(lookup.key) ?? null : null;
  }

  /** Whether the checks shown may not fit the lookups as they are. */
  get checkStale(): boolean {
    return this.checkPending || this.checkFailed;
  }

  labelColumns(check: ImportModuleLookupCheck): string[] {
    return checkLabelColumns(check);
  }

  // --- the code table ---

  private codeRowSets(lookup: LookupDefinition, check: ImportModuleLookupCheck) {
    const cached = this.codeRowCache.get(lookup.key);
    const aliases = lookup.lookupAliasesControl.value;
    const keyOptions = lookup.lookupKeyOptionsControl.value;
    const caseInsensitive = lookup.lookupCaseInsensitiveControl.value;
    if (cached?.check === check && cached.aliases === aliases && cached.keyOptions === keyOptions
        && cached.caseInsensitive === caseInsensitive) {
      return cached;
    }
    const rows = lookupCodeRows(lookup, check);
    const misses = rows.filter(row => row.missed);
    const sets = {
      check, aliases, keyOptions, caseInsensitive, rows, misses,
      topHits: check.missed_rows || misses.length ? rows.filter(row => !row.missed).slice(0, TOP_CODES_SHOWN) : [],
      capped: !listsEveryCode(check),
    };
    this.codeRowCache.set(lookup.key, sets);
    return sets;
  }

  codeRows(lookup: LookupDefinition, check: ImportModuleLookupCheck): LookupCodeRow[] {
    return this.codeRowSets(lookup, check).rows;
  }

  missRows(lookup: LookupDefinition, check: ImportModuleLookupCheck): LookupCodeRow[] {
    return this.codeRowSets(lookup, check).misses;
  }

  /** The most frequent hits, for a check that missed a code. Without a
   * miss the code table opens on every code, most rows first, so it
   * already lists them. */
  topHits(lookup: LookupDefinition, check: ImportModuleLookupCheck): LookupCodeRow[] {
    return this.codeRowSets(lookup, check).topHits;
  }

  /** Whether the check lists only the most frequent codes: its misses
   * are then those among them, not every code the lookup misses. */
  codesCapped(lookup: LookupDefinition, check: ImportModuleLookupCheck): boolean {
    return this.codeRowSets(lookup, check).capped;
  }

  /** Uten treff while the code table lists a missed code, else Alle,
   * until the operator picks one. */
  codeFilter(lookup: LookupDefinition, check: ImportModuleLookupCheck): LookupCodeFilter {
    return this.codeFilters.get(lookup.key) ?? (this.missRows(lookup, check).length ? 'misses' : 'all');
  }

  setCodeFilter(lookup: LookupDefinition, filter: LookupCodeFilter): void {
    this.codeFilters.set(lookup.key, filter);
  }

  /** The rows the code table shows under its filter: the first
   * `CODE_ROWS_SHOWN` until the operator asks for all. */
  shownCodeRows(lookup: LookupDefinition, check: ImportModuleLookupCheck): LookupCodeRow[] {
    const rows = this.filteredCodeRows(lookup, check);
    return this.allCodesShown.has(lookup.key) ? rows : rows.slice(0, CODE_ROWS_SHOWN);
  }

  private filteredCodeRows(lookup: LookupDefinition, check: ImportModuleLookupCheck): LookupCodeRow[] {
    return this.codeFilter(lookup, check) === 'misses' ? this.missRows(lookup, check) : this.codeRows(lookup, check);
  }

  /** Whether the code table leaves out rows of its own filter. */
  hidesFilteredCodes(lookup: LookupDefinition, check: ImportModuleLookupCheck): boolean {
    return !this.allCodesShown.has(lookup.key) && this.filteredCodeRows(lookup, check).length > CODE_ROWS_SHOWN;
  }

  /** Whether Uten treff leaves out the codes with a hit. */
  hidesHitCodes(lookup: LookupDefinition, check: ImportModuleLookupCheck): boolean {
    return this.codeFilter(lookup, check) === 'misses' && this.missRows(lookup, check).length < check.codes.length;
  }

  /** Every row of the code table's filter. */
  showAllFilteredCodes(lookup: LookupDefinition): void {
    this.allCodesShown.add(lookup.key);
  }

  /** Every code, hits included. */
  showAllCodes(lookup: LookupDefinition): void {
    this.codeFilters.set(lookup.key, 'all');
    this.allCodesShown.add(lookup.key);
  }

  /** Whether a code is a cell of the code column as it stands - not one
   * composed by a key template - so it can be read as blank there. */
  codeIsCell(lookup: LookupDefinition): boolean {
    return !lookupPayload(lookup, this.columnNames)?.key_parts;
  }

  // --- a missed code's fixes ---

  isPickerOpen(lookup: LookupDefinition, code: string): boolean {
    return this.pickerTarget?.lookup === lookup && this.pickerTarget.code === code;
  }

  /** Opens the table-key picker on `code`'s row, closing any other. */
  openPicker(lookup: LookupDefinition, code: string): void {
    this.pickerTarget = {lookup, code};
    this.pickerControl.setValue('');
  }

  closePicker(): void {
    this.pickerTarget = null;
  }

  /** The table keys the open picker offers for its search text, found
   * once per check and text. */
  pickerOptions(check: ImportModuleLookupCheck): ImportModuleLookupKeyLabels[] {
    const text = this.pickerControl.value;
    const cached = this.pickerOptionCache;
    if (cached?.check === check && cached.text === text) {
      return cached.options;
    }
    const options = matchingTableKeys(check, text, TABLE_KEYS_OFFERED);
    this.pickerOptionCache = {check, text, options};
    return options;
  }

  /** Lets `code` stand for the table's `key`, and closes the picker. */
  pickTableKey(lookup: LookupDefinition, code: string, key: string): void {
    setAlias(lookup, code, key);
    this.closePicker();
  }

  /** Drops the alias that hides the table's own row for `code`. */
  useTableCode(lookup: LookupDefinition, code: string): void {
    removeAlias(lookup, code);
  }

  // --- the key options ---

  isKeyOptionsOpen(lookup: LookupDefinition): boolean {
    return this.keyOptionsOpen.has(lookup.key);
  }

  toggleKeyOptions(lookup: LookupDefinition): void {
    if (!this.keyOptionsOpen.delete(lookup.key)) {
      this.keyOptionsOpen.add(lookup.key);
    }
  }

  keyOptionsInUse(lookup: LookupDefinition): boolean {
    return lookupKeyOptionsInUse(lookup, this.columnNames);
  }

  private get columnNames(): Set<string> {
    if (this.columnNameCache?.columns !== this.columns) {
      this.columnNameCache = {columns: this.columns, names: new Set(this.columns.map(column => column.name))};
    }
    return this.columnNameCache.names;
  }
}
